import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Cookies from "js-cookie";

export const bookingApi = createApi({
  reducerPath: "bookingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BASE_URL}`,
    prepareHeaders: (headers, { getState }) => {
      const { auth } = getState();
      const token = auth.user.token;
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", `application/json;charset=UTF-8`);
      return headers;
    },
  }),
  tagTypes: ["orders", "tilesData", "payment", "history", "vehicle"],
  endpoints: (builder) => ({
    todaysOrders: builder.query({
      query: (data) => {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split(".")[0]; // Split to remove milliseconds
        return {
          url: "/Booking/GetOrders",
          method: "GET",
          params: {
            ...data,
            specificDate: formattedDate,
          },
        };
      },
      providesTags: ["orders"],
    }),
    orders: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetOrders",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders"],
    }),
    ordersByDate: builder.query({
      query: (data) => {
        return {
          url: "/Booking/OrderMetricsByWeek",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders"],
    }),
    orderById: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetReservations3",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders"],
    }),
    orderByCustomerId: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetOrders",
          method: "GET",
          params: {
            customerID: data,
          },
        };
      },
      providesTags: ["orders"],
    }),
    tilesData: builder.query({
      query: (data) => {
        return {
          url: "/Booking/AllTimeTileData",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["tilesData"],
    }),
    createOrder: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/CreateOrders",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    createReservation: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/CreateReservation",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    updateReservation: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/UpdateReservations",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    paymentTypes: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetPaymentTypes",
          method: "GET",
          params: data,
        };
      },
    }),
    discountsTypes: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetDiscountsTypes",
          method: "GET",
          params: data,
        };
      },
    }),
    status: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetStatusDescriptions",
          method: "GET",
          params: data,
        };
      },
    }),
    technicians: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetTechnicians",
          method: "GET",
          params: data,
        };
      },
    }),
    assignTechnician: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/AssignTechnician",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    updateStatus: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/update-status",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    paymentById: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetPayments",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["payment"],
    }),
    addPayment: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/CreatePayment",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["payment"],
    }),
    history: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetHistoryByKeyId",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["history"],
    }),
    createHistory: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/CreateHistory",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["history"],
    }),
    getVehicle: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetVehicleDetails",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders", "vehicle"],
    }),
    createVehicle: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/CreateVehicleDetails",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders", "vehicle"],
    }),
    updateVehicle: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/update-vehicle-details",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders", "vehicle"],
    }),
    deleteVehicleImage: builder.mutation({
      query: (data) => {
        return {
          url: `/Booking/DeleteVehicleImage?keyID=${data}`,
          method: "POST",
        };
      },
      invalidatesTags: ["orders", "vehicle"],
    }),
    checkIn: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetCheckInOrders",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders"],
    }),
    checkOut: builder.query({
      query: (data) => {
        return {
          url: "/Booking/GetCheckOutOrders",
          method: "GET",
          params: data,
        };
      },
      providesTags: ["orders"],
    }),

    requestStock: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/RequestStock",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),
    approveStock: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/ApproveStock",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["orders"],
    }),

    // =================================================================
    // OFF-DAYS API ENDPOINTS
    // =================================================================

    // Get off-days for a specific order
    getOffDays: builder.query({
      query: (orderId) => {
        return {
          url: `/Booking/off-days/${orderId}`,
          method: "GET",
        };
      },
      providesTags: (result, error, orderId) => [
        { type: "orders", id: orderId },
        { type: "orders", id: "LIST" },
      ],
    }),

    // Get off-days history for a specific order
    getOffDaysHistory: builder.query({
      query: (orderId) => {
        return {
          url: `/Booking/off-days-history/${orderId}`,
          method: "GET",
        };
      },
      providesTags: (result, error, orderId) => [
        { type: "orders", id: orderId },
        { type: "orders", id: "LIST" },
      ],
    }),

    // Process off-days for an order
    processOffDays: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/process-off-days",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "orders", id: arg.orderId },
        { type: "orders", id: "LIST" },
      ],
    }),

    // Enhanced update order status with off-days processing
    updateOrderStatusWithOffDays: builder.mutation({
      query: (data) => {
        return {
          url: "/Booking/update-status-with-off-days",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "orders", id: arg.orderId },
        { type: "orders", id: "LIST" },
      ],
    }),

    // =================================================================
    // CREDIT NOTE API ENDPOINTS
    // =================================================================

    // Get credit note by ID

    // Credit items in reservation
    creditItems: builder.mutation({
      query: ({ orderId, roomId, creditedAmount }) => {
        return {
          url: "/Booking/credit-items",
          method: "POST",
          body: {
            orderId,
            roomId,
            creditedAmount,
          },
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "orders", id: arg.orderId },
        { type: "orders", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useTodaysOrdersQuery,
  useOrdersQuery,
  useOrderByIdQuery,
  useOrderByCustomerIdQuery,
  useTilesDataQuery,
  useCreateOrderMutation,
  usePaymentTypesQuery,
  useDiscountsTypesQuery,
  useStatusQuery,
  useTechniciansQuery,
  useAssignTechnicianMutation,
  useUpdateStatusMutation,
  usePaymentByIdQuery,
  useAddPaymentMutation,
  useHistoryQuery,
  useCreateHistoryMutation,
  useGetVehicleQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleImageMutation,
  useCheckInQuery,
  useOrdersByDateQuery,
  useRequestStockQuery,
  useCheckOutQuery,
  useCreateReservationMutation,
  useUpdateReservationMutation,
  useRequestStockMutation,
  useApproveStockMutation,
  // Off-days API hooks
  useGetOffDaysQuery,
  useGetOffDaysHistoryQuery,
  useProcessOffDaysMutation,
  useUpdateOrderStatusWithOffDaysMutation,
  // Credit Items API hook
  useCreditItemsMutation,
} = bookingApi;
